import {
  Pos,
  Po,
  PoDesc,
  ProcRec,
  Progress,
  FlattenedObject,
  SortCondition,
  ProductTypes,
} from "../models/Po";
import dayjs from "dayjs";
import { Auth } from "aws-amplify";

export function flattenPoData(
  poData: Pos,
  holidays: string[]
): FlattenedObject[] {
  const flattenedArray: FlattenedObject[] = [];

  poData.forEach((po: Po) => {
    const { po_descs, ...po_base } = po;

    po_descs.forEach((po_desc: PoDesc) => {
      const {
        proc_recs,
        progress = [],
        interlayer = [],
        marks = [],
        final_procs = [], // デフォルト値を空の配列に設定
        ...po_desc_base
      } = po_desc;

      let mach_abbr_names = "";
      let has_lam = 0;
      let has_ig = 0;
      let has_film = 0;
      let has_soak = 0;
      let next_mach_seq = 0;
      let next_mach_name = "";

      let sort_order = 0;

      if (progress.length > 0) {
        mach_abbr_names = progress
          .filter((p: Progress) => p.status !== 4)
          .map((p: Progress) => p.mach_abbr_name)
          .join(":");
        has_lam = progress.some(
          (p: Progress) =>
            p.mach_code === "153" ||
            p.mach_code === "553" ||
            p.mach_code === "555"
        )
          ? 1
          : 0;
        has_ig = progress.some(
          (p: Progress) =>
            p.mach_code === "159" ||
            p.mach_code === "559" ||
            p.mach_code === "561"
        )
          ? 1
          : 0;
        has_film = progress.some(
          (p: Progress) => p.mach_code === "163" || p.mach_code === "563"
        )
          ? 1
          : 0;
        has_soak = progress.some(
          (p: Progress) =>
            p.mach_code === "148" ||
            p.mach_code === "165" ||
            p.mach_code === "548" ||
            p.mach_code === "565"
        )
          ? 1
          : 0;

        // 受注ＮＯの未完了工程の先頭の工順を求めるnext_mach_seqとして求める。
        const filteredProgress = progress.filter(
          (p: Progress) => p.mach_code !== "552"
        );
        let maxSeqElement = filteredProgress
          .filter((p: Progress) => p.status === 4)
          .reduce((max, p) => (p.mach_seq > max.mach_seq ? p : max), {
            mach_seq: -1,
          });
        let nextElement = filteredProgress.find(
          (p: Progress) => p.mach_seq > maxSeqElement.mach_seq
        );
        next_mach_seq = nextElement ? nextElement.mach_seq : 0;

        // Find the next_mach_name based on next_mach_seq
        const nextMachProgress = progress.find(
          (p) => p.mach_seq === next_mach_seq
        );
        next_mach_name = nextMachProgress ? nextMachProgress.mach_name : "";
      }

      const proc_date = calculateProcDate(
        po_desc_base.ship_date,
        has_lam,
        has_ig,
        has_soak,
        has_film,
        holidays
      ); // calculateProcDate関数の型も適切に定義してください

      // Process final_procs
      let tempering_schedule_start_time: string | undefined;
      let laminating_schedule_start_time: string | undefined;
      let insulating_schedule_start_time: string | undefined;

      // final_procs が配列であることを確認してから処理
      if (Array.isArray(final_procs)) {
        final_procs.forEach((final_proc) => {
          switch (final_proc.proc_code) {
            case "161":
              tempering_schedule_start_time = final_proc.schedule_start_time;
              break;
            case "193":
              laminating_schedule_start_time = final_proc.schedule_start_time;
              break;
            case "213":
              insulating_schedule_start_time = final_proc.schedule_start_time;
              break;
          }
        });
      }

      proc_recs.forEach((proc_rec: ProcRec) => {
        const ready = proc_rec.mach_seq === next_mach_seq ? 1 : 0;

        // Find the index of the current mach_seq in progress
        const currentIndex = progress.findIndex(
          (p) => p.mach_seq === proc_rec.mach_seq
        );

        // Get the next progress item if it exists
        const nextProgress =
          currentIndex !== -1 && currentIndex < progress.length - 1
            ? progress[currentIndex + 1]
            : null;

        const following_mach_seq = nextProgress ? nextProgress.mach_seq : 0;
        const following_mach_name = nextProgress
          ? nextProgress.mach_abbr_name
          : "";

        const flattenedObject: FlattenedObject = {
          ...po_base,
          ...po_desc_base,
          ...proc_rec,
          mach_abbr_names,
          has_lam,
          has_ig,
          has_film,
          has_soak,
          ready,
          proc_date,
          next_mach_seq,
          next_mach_name,
          following_mach_seq,
          following_mach_name,
          sort_order,
          interlayer: interlayer.join(":"),
          marks: marks,
          tempering_schedule_start_time,
          laminating_schedule_start_time,
          insulating_schedule_start_time,
        };
        flattenedArray.push(flattenedObject);
      });
    });
  });

  return updateSortOrder(flattenedArray);
}

export function updateSortOrder(
  flattenedArray: FlattenedObject[]
): FlattenedObject[] {
  // 中断→作中→QRスキャン→未作の順にソートする
  flattenedArray.forEach((item) => {
    item.sort_order = 0;
    // ソートポイント3 超緊急かつ加工開始時刻無し
    if (item.urgency_level === 2 && item.schedule_start_time === "") {
      item.sort_order = 3;
      // 未作 チェックありなしはこの時点ではチェックされていないので考慮しない
    } else if (item.proc_status === 0) {
      item.sort_order = 0;
    } else if (
      item.proc_status === 1 ||
      item.proc_status === 2 ||
      item.proc_status === 3
    ) {
      item.sort_order = 2;
    }
    /*
    if (item.proc_status === 2) {
      // 中断
      item.sort_order = 100;
    } else if (item.proc_status === 1 || item.proc_status === 3) {
      // 作業中 or 再開
      item.sort_order = 10;
    }
    */
  });
  return flattenedArray;
}

export function sortFlattenedObject(
  flattenedArray: FlattenedObject[]
): FlattenedObject[] {
  const sortConditions: SortCondition[] = [
    { field: "sort_order", order: "desc" },
    // { field: 'urgency_level', order: 'desc' },
    { field: "proc_date", order: "asc" },
    // { field: 'ship_date', order: 'asc' },
    { field: "thickness", order: "asc" },
    { field: "po_data_num", order: "asc" },
  ];
  return multiSort(flattenedArray, sortConditions);
}

export function calculateProcDate(
  ship_date: string,
  has_lam: number,
  has_ig: number,
  has_soak: number,
  has_film: number,
  holidays: string[]
): string | null {
  let daysBefore = 1;
  if (has_lam === 1) {
    daysBefore += 2;
  }
  if (has_ig === 1) {
    daysBefore += 2;
  }
  if (has_soak === 1) {
    daysBefore += 1;
  }
  if (has_film === 1) {
    daysBefore += 1;
  }

  if (daysBefore > 0 && ship_date) {
    let procDate = new Date(ship_date);

    // 休日を考慮する
    const yyyy_mm_dd = (date: Date): string => {
      const yyyy = date.getFullYear();
      const mm = String(date.getMonth() + 1).padStart(2, "0");
      const dd = String(date.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    };

    while (daysBefore > 0) {
      procDate = new Date(procDate.getTime() - 86400000); // 1日減らす
      const dateStr = yyyy_mm_dd(procDate);
      if (!holidays.includes(dateStr)) {
        daysBefore--;
      }
    }

    return yyyy_mm_dd(procDate);
  }

  return null;
}
// export function convertToCSV(rowData: any[], columnNames: string): string {
//   const rows = rowData.map(row => Object.values(row).join('\t')).join('\n');
//   return `${columnNames}\n${rows}`;
// }

export function multiSort(data: any[], conditions: SortCondition[]): any[] {
  return [...data].sort((a, b) => {
    for (const condition of conditions) {
      const { field, order } = condition;
      const valueA = a[field as keyof typeof a];
      const valueB = b[field as keyof typeof b];

      if (valueA == null || valueB == null) continue; // nullまたはundefinedの場合は次の条件に進む

      if (valueA < valueB) {
        return order === "asc" ? -1 : 1;
      }
      if (valueA > valueB) {
        return order === "asc" ? 1 : -1;
      }
    }
    return 0;
  });
}

export const getCurrentDateOffset = (
  monthsOffset: number,
  yearsOffset: number,
  daysOffset: number = 0
) => {
  const date = new Date();
  date.setMonth(date.getMonth() + monthsOffset);
  date.setFullYear(date.getFullYear() + yearsOffset);
  date.setDate(date.getDate() + daysOffset);
  // 日付をYYYY-MM-DD形式に変換
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0"); // January is 0!
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

export const getCurrentDateOffsetDate = (
  monthsOffset: number,
  yearsOffset: number
) => {
  const date = new Date();
  date.setMonth(date.getMonth() + monthsOffset);
  date.setFullYear(date.getFullYear() + yearsOffset);
  return date;
};

/**
 * YYYY-MM-DDをDate型に変換
 */
export const stringToDate = (dateStr: string) => {
  return dayjs(dateStr).toDate();
};
export const dateToString = (date: Date) => {
  return dayjs(date).format("YYYY-MM-DD");
};
export const dateToDateTimeString = (date: Date) => {
  return dayjs(date).format("MM/DD HH:mm:ss");
};
export const dateToTimestampString = (date: Date) => {
  return dayjs(date).format("YYYY-MM-DDTHH:mm:ss");
};

export const stringDateFormat = (dateStr: string) => {
  return dayjs(dateStr).format("YYYY年M月D日 HH:mm");
};

// API認証用トークン取得
export const getToken: () => Promise<string> = async () => {
  try {
    const currentSession = await Auth.currentSession().then();
    const idToken = currentSession.getIdToken().getJwtToken();
    return idToken;
  } catch (e) {
    console.log(e);
    return "";
  }
};

// ユーザ名取得
export const getUserName: () => Promise<string> = async () => {
  const currentSession = await Auth.currentSession().then();
  return currentSession.getAccessToken().payload.username;
};

// 画像サイズ取得
export const getImageDimensions = (base64Image: string) => {
  return new Promise<{ w: number; h: number }>(function (resolved, rejected) {
    var i = new Image();
    i.onload = function () {
      resolved({ w: i.width, h: i.height });
    };
    i.src = base64Image;
  });
};

/**
 * 任意の桁で四捨五入する関数
 * @param {number} value 四捨五入する数値
 * @param {number} base どの桁で四捨五入するか（10→10の位、0.1→小数第１位）
 * @return {number} 四捨五入した値
 */
export const orgRound = (value: number, base: number) => {
  return Math.round(value * base) / base;
};

const ip2bin = (ip: string) =>
  ip
    .split(".")
    .map((e) => Number(e).toString(2).padStart(8, "0"))
    .join("");
const ip2long = (ip: string) => parseInt(ip2bin(ip), 2);
/**
 * IPアドレスが指定した範囲内(acceptIp/cidr)にあるかどうか判定
 * @param remoteIp サーバのIP
 * @param acceptIp 許可するIP
 * @param cidr cidr
 * @returns
 */
export const inRange = (
  remoteIp: string | undefined,
  acceptIp: string | undefined,
  cidr: string | undefined
) => {
  if (remoteIp === undefined) {
    console.error("remoteIpが取得できません。環境変数を確認してください。");
    return false;
  }
  if (acceptIp === undefined) {
    console.error("acceptIpが取得できません。環境変数を確認してください。");
    return false;
  }
  if (cidr === undefined) {
    console.error("cidrが取得できません。環境変数を確認してください。");
    return false;
  }
  console.log(`remoteIp:${remoteIp}, acceptIp:${acceptIp}, cidr:${cidr}`);
  const remoteIp_ = ip2long(remoteIp);
  const acceptIp_ = ip2long(acceptIp);
  const remoteIpNetwork = remoteIp_ >>> (32 - Number(cidr));
  const acceptIpNetwork = acceptIp_ >>> (32 - Number(cidr));
  return remoteIpNetwork === acceptIpNetwork;
};
